import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../../components/@extended/CustomButton';
import { 
  useAddFianceAdminMutation, 
  useAddSponsorAdminMutation, 
  useAddDataManagerMutation,
  useAddSponsorLevelUserMutation 
} from '../../store/slices/apiSlice';
import { openMessage } from '../../store/slices/showMessageSlice';
import Phone, {phoneValidationSchema} from '../../components/common/Phone';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '../../util/util';
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-google-places-autocomplete';
import GooglePlacesAutocompleteComponent from '../../components/common/GooglePlacessAutocomplete';

// User type options for sponsor-level users
const USER_TYPE_OPTIONS = [
  { value: 'data_manager', label: 'Data Manager' },
  { value: 'site_monitor', label: 'Site Monitor' },
];

// Base validation schema
const baseValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email address')
    .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE),
  contactNumber: phoneValidationSchema.concat(Yup.string().required('Contact number is required')),
});

// Validation schema with userType (for sponsor-level users)
const sponsorLevelUserValidationSchema = baseValidationSchema.shape({
  userType: Yup.string().required('User Type is required'),
});

const AdminDialog = ({ open, onClose, sponsorId, type, showUserTypeDropdown = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [addAdmin] = useAddSponsorAdminMutation();
  const [addFinAdmin] = useAddFianceAdminMutation();
  const [addDataManager] = useAddDataManagerMutation();
  const [addSponsorLevelUser] = useAddSponsorLevelUserMutation();
  const [address, setAddress] = useState(null);
  const [addressError, setAddressError] = useState('');
  const [addressDetails, setAddressDetails] = useState();

  // Determine which validation schema to use
  const validationSchema = (type === 'sponsor_level_user' && showUserTypeDropdown) 
    ? sponsorLevelUserValidationSchema 
    : baseValidationSchema;

  const openMessageNotification = (message) => {
    dispatch(openMessage({ message: message.message, messageSeverity: message.type }));
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!address) {
      setAddressError('Address is required');
      setSubmitting(false);
      return;
    }
    try {
      let result;
      let successMessage = 'User Created Successfully';

      if (type === 'fi_admin') {
        result = await addFinAdmin({ payload: { ...values, userName: values.email, ...addressDetails }, sponsorId });
        successMessage = 'Finance Admin Created Successfully';
      } else if (type === 'data_manager') {
        result = await addDataManager({ payload: { ...values, userName: values.email, ...addressDetails }, sponsorId });
        successMessage = 'Data Manager Created Successfully';
      } else if (type === 'sponsor_level_user') {
        // Use the new unified API for sponsor-level users (Data Manager & Site Monitor)
        result = await addSponsorLevelUser({ 
          payload: { ...values, userName: values.email, ...addressDetails }, 
          sponsorId 
        });
        const userTypeLabel = USER_TYPE_OPTIONS.find(opt => opt.value === values.userType)?.label || 'User';
        successMessage = `${userTypeLabel} Created Successfully`;
      } else {
        result = await addAdmin({ payload: { ...values, userName: values.email, ...addressDetails }, sponsorId });
        successMessage = 'Sponsor Admin Created Successfully';
      }
      
      if (result.error) {
        let errorMessage = 'An error occurred';
        if (result.error.status === 401) {
          errorMessage = 'Unauthorized access';
        } else if (result.error.data?.message?.includes('duplicate')) {
          errorMessage = 'Email already exists';
        } else if (result.error.data?.message) {
          errorMessage = result.error.data.message;
        }
  
        openMessageNotification({
          message: errorMessage,
          type: 'error',
        });
      } else {
        navigate(`/sponsorDisplay/${sponsorId}`);
        openMessageNotification({
          message: successMessage,
          type: 'success',
        });
        setAddress(null);
        resetForm();
        onClose();
      }
    } catch (error) {
      openMessageNotification({
        message: 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };
  const handleClose = ({ resetForm }) => {
    onClose();
    setAddress(null);
    setAddressDetails(null);
    setAddressError('');
    resetForm();
  };

  useEffect(() => {
    if (address) {
      setAddressError('');
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) => setAddressDetails({ latitude: lat, longitude: lng, address: address.label }));
    } else {
      setAddressDetails(null);
    }
  }, [address]);

  // Determine dialog title based on type
  const getDialogTitle = () => {
    if (type === 'fi_admin') return 'Add Finance Admin';
    if (type === 'data_manager') return 'Add Data Manager';
    if (type === 'sponsor_level_user') return 'Add Data Manager / Site Monitor';
    return 'Add Sponsor Admin';
  };

  // Determine initial values based on type
  const getInitialValues = () => {
    const baseValues = {
      firstName: '',
      lastName: '',
      email: '',
      contactNumber: '',
    };

    if (type === 'sponsor_level_user' && showUserTypeDropdown) {
      return { ...baseValues, userType: '' };
    }

    return baseValues;
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, touched, errors, values, handleChange, resetForm, setFieldValue }) => (
        <Dialog open={open} onClose={() => handleClose({ resetForm })}>
          <DialogTitle>
            {getDialogTitle()}
          </DialogTitle>
          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            onClick={() => handleClose({ resetForm })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} pb={2}>
                  <TextField
                    fullWidth
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={handleChange}
                    size="small"
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} md={6} pb={2}>
                  <TextField
                    fullWidth
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={handleChange}
                    size="small"
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                </Grid>
                <Grid item xs={12} md={6} pb={2}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    size="small"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={6} pb={2}>
                  <Field name="contactNumber" component={Phone} label="Contact Number" />
                </Grid>
                {/* Conditional User Type Dropdown for sponsor-level users */}
                {type === 'sponsor_level_user' && showUserTypeDropdown && (
                  <Grid item xs={12} md={6} pb={2}>
                    <FormControl 
                      fullWidth 
                      size="small" 
                      error={touched.userType && Boolean(errors.userType)}
                    >
                      <InputLabel id="user-type-label">User Type</InputLabel>
                      <Select
                        labelId="user-type-label"
                        id="userType"
                        name="userType"
                        value={values.userType}
                        label="User Type"
                        onChange={handleChange}
                      >
                        {USER_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.userType && errors.userType && (
                        <FormHelperText>{errors.userType}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md={type === 'sponsor_level_user' && showUserTypeDropdown ? 6 : 12} pb={2}>
                  <Grid item xs={12} md={12}>
                    <GooglePlacesAutocompleteComponent address={address} setAddress={setAddress} />
                    {addressError && (
                      <Typography sx={{ fontSize: '12px', color: '#C62828', mt: 1 }}>{addressError}</Typography>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} mb={2}>
                  <Box paddingRight={1.5}>
                    <CustomButton variant="outlined" onClick={() => handleClose({ resetForm })}>
                      Cancel
                    </CustomButton>
                  </Box>
                  <Box>
                    <CustomButton type="submit" variant="contained" disabled={isSubmitting}>
                      Submit
                    </CustomButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};

const CreateAdmin = ({ sponsorId, type, showUserTypeDropdown = false }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Determine button label based on type
  const getButtonLabel = () => {
    if (type === 'fi_admin') return '+ Finance Admin';
    if (type === 'data_manager') return '+ Data Manager';
    if (type === 'sponsor_level_user') return '+ Add User';
    return '+ Sponsor Admin';
  };

  return (
    <Grid>
      <CustomButton variant="contained" onClick={handleOpen}>
        {getButtonLabel()}
      </CustomButton>
      <AdminDialog 
        open={open} 
        onClose={handleClose} 
        sponsorId={sponsorId} 
        type={type} 
        showUserTypeDropdown={showUserTypeDropdown}
      />
    </Grid>
  );
};

export default CreateAdmin;
